import React, { useState } from "react";

const Bootcamp = () => {
  const [isModule1Open, setIsModule1Open] = useState(false);
  const [isModule2Open, setIsModule2Open] = useState(false);
  const [isModule3Open, setIsModule3Open] = useState(false);
  const [showCodeForm, setShowCodeForm] = useState(false);
  const [currentModule, setCurrentModule] = useState(null); // Tracks which module is being unlocked
  const [enteredCode, setEnteredCode] = useState("");

  const handleModuleClick = (module) => {
    if (module === 1) {
      // Module 1 opens/closes without a code
      setIsModule1Open(!isModule1Open);
    } else if (module === 2) {
      // Module 2: If already open, close it without code; otherwise, prompt for code
      if (isModule2Open) {
        setIsModule2Open(false);
      } else {
        setCurrentModule(module);
        setShowCodeForm(true);
      }
    } else if (module === 3) {
      // Module 3: If already open, close it without code; otherwise, prompt for code
      if (isModule3Open) {
        setIsModule3Open(false);
      } else {
        setCurrentModule(module);
        setShowCodeForm(true);
      }
    }
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    const correctCode = currentModule === 2 ? "456" : "147"; // Correct code for Module 2 or 3

    if (enteredCode === correctCode) {
      // Open the respective module
      if (currentModule === 2) {
        setIsModule2Open(true);
      } else if (currentModule === 3) {
        setIsModule3Open(true);
      }
      setShowCodeForm(false);
      setEnteredCode(""); // Reset the code input
    } else {
      alert("Incorrect code. Please try again.");
      setEnteredCode(""); // Reset the code input on incorrect attempt
    }
  };

  const handleCodeFormClose = () => {
    setShowCodeForm(false);
    setEnteredCode(""); // Reset the code input
  };

  return (
    <div className="container mx-auto px-4 py-6 sm:px-6 md:px-8 lg:py-10">
      <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-green-600 mb-4 md:mb-6">
        Inventory Management Bootcamp
      </h1>
      <p className="text-base sm:text-lg text-gray-700 mb-4 md:mb-6">
        Welcome to the Inventory Management Bootcamp! This 15-hour course covers building a complete
        inventory management system with sign-in/sign-out, CRUD operations, a Django web server, and
        a MySQL database. Each module includes 5 hours of detailed content with hands-on practicals.
      </p>

      <div className="space-y-4 md:space-y-6">
        {/* Module 1 */}
        <div className="bg-yellow-100 p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-2 sm:mb-0">
              Module 1: Setting Up the Environment
            </h2>
            <button
              onClick={() => handleModuleClick(1)}
              className="bg-green-600 text-white px-3 py-1 sm:px-4 sm:py-2 rounded-md hover:bg-green-700 transition-colors duration-300 w-full sm:w-auto"
            >
              {isModule1Open ? "Close Module" : "Start Module 1"}
            </button>
          </div>
          {isModule1Open && (
            <div className="mt-4 text-gray-600">
              <p className="mb-4 text-sm sm:text-base">
                Duration: 5 hours | Learn to set up Django, MySQL, and React for inventory management.
              </p>
              <ul className="list-disc list-inside mb-4 space-y-4">
                <li>
                  <strong className="text-sm sm:text-base">Hour 1-2: Installation and Setup</strong>
                  <p className="text-sm sm:text-base">
                    Install Python, Django, MySQL, and Node.js. Configure your development environment.
                  </p>
                  <pre className="bg-gray-100 p-2 mt-2 rounded text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap">
                    {`# Install dependencies
pip install django==4.2 djangorestframework mysqlclient
npm install -g create-react-app

# Verify installations
python --version
django-admin --version
mysql --version
node -v`}
                  </pre>
                </li>
                <li>
                  <strong className="text-sm sm:text-base">Hour 2-3: Database Configuration</strong>
                  <p className="text-sm sm:text-base">
                    Create a MySQL database and connect it to Django. Test the connection.
                  </p>
                  <pre className="bg-gray-100 p-2 mt-2 rounded text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap">
                    {`# MySQL commands
CREATE DATABASE inventory_management;
CREATE USER 'inventory_user'@'localhost' IDENTIFIED BY 'secure_password';
GRANT ALL PRIVILEGES ON inventory_management.* TO 'inventory_user'@'localhost';
FLUSH PRIVILEGES;

# settings.py
DATABASES = {
    'default': {
        'ENGINE': 'django.db.backends.mysql',
        'NAME': 'inventory_management',
        'USER': 'inventory_user',
        'PASSWORD': 'secure_password',
        'HOST': 'localhost',
        'PORT': '3306',
    }
}`}
                  </pre>
                </li>
                <li>
                  <strong className="text-sm sm:text-base">Hour 3-4: React Frontend Setup</strong>
                  <p className="text-sm sm:text-base">
                    Set up a React project with routing and integrate it with the Django backend.
                  </p>
                  <pre className="bg-gray-100 p-2 mt-2 rounded text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap">
                    {`# Create React app
npx create-react-app frontend
cd frontend
npm install react-router-dom

# Basic App.js
import { BrowserRouter, Routes, Route } from 'react-router-dom';
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/bootcamp" element={<Bootcamp />} />
      </Routes>
    </BrowserRouter>
  );
}`}
                  </pre>
                </li>
                <li>
                  <strong className="text-sm sm:text-base">Hour 4-5: Authentication Setup</strong>
                  <p className="text-sm sm:text-base">
                    Implement sign-in and sign-out using Django authentication.
                  </p>
                  <pre className="bg-gray-100 p-2 mt-2 rounded text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap">
                    {`# settings.py
INSTALLED_APPS = [
    ...,
    'rest_framework',
    'rest_framework.authtoken',
    'django.contrib.auth',
]

# urls.py
from django.urls import path
from rest_framework.authtoken.views import obtain_auth_token

urlpatterns = [
    path('api/token/', obtain_auth_token, name='api_token_auth'),
]

# Test login
curl -X POST -d "username=testuser&password=testpass" http://127.0.0.1:8000/api/token/`}
                  </pre>
                </li>
                <li>
                  <strong className="text-sm sm:text-base">Hour 5: Hands-On Exercise</strong>
                  <p className="text-sm sm:text-base">
                    Create a Django app, define a model, and connect to MySQL.
                  </p>
                  <pre className="bg-gray-100 p-2 mt-2 rounded text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap">
                    {`# Create and configure app
python manage.py startapp inventory
python manage.py makemigrations
python manage.py migrate

# models.py
from django.db import models
class Item(models.Model):
    name = models.CharField(max_length=100)
    quantity = models.IntegerField()
    price = models.DecimalField(max_digits=10, decimal_places=2)

# Verify
python manage.py shell
from inventory.models import Item
Item.objects.all() # Should be empty`}
                  </pre>
                  <p className="text-sm sm:text-base">
                    <strong>Troubleshooting:</strong> If connection fails, check MySQL credentials or run `mysqladmin ping`.
                  </p>
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Module 2 */}
        <div className="bg-blue-100 p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-2 sm:mb-0">
              Module 2: CRUD Operations
            </h2>
            <button
              onClick={() => handleModuleClick(2)}
              className="bg-green-600 text-white px-3 py-1 sm:px-4 sm:py-2 rounded-md hover:bg-green-700 transition-colors duration-300 w-full sm:w-auto"
            >
              {isModule2Open ? "Close Module" : "Start Module 2"}
            </button>
          </div>
          {isModule2Open && (
            <div className="mt-4 text-gray-600">
              <p className="mb-4 text-sm sm:text-base">
                Duration: 5 hours | Build Create, Read, Update, and Delete functionality for inventory.
              </p>
              <ul className="list-disc list-inside mb-4 space-y-4">
                <li>
                  <strong className="text-sm sm:text-base">Hour 1-2: Model and Serializer</strong>
                  <p className="text-sm sm:text-base">
                    Define the Item model and create a serializer for API interactions.
                  </p>
                  <pre className="bg-gray-100 p-2 mt-2 rounded text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap">
                    {`# models.py
from django.db import models
class Item(models.Model):
    name = models.CharField(max_length=100)
    quantity = models.IntegerField()
    price = models.DecimalField(max_digits=10, decimal_places=2)

# serializers.py
from rest_framework import serializers
from .models import Item

class ItemSerializer(serializers.ModelSerializer):
    class Meta:
        model = Item
        fields = '__all__'`}
                  </pre>
                </li>
                <li>
                  <strong className="text-sm sm:text-base">Hour 2-3: CRUD APIs</strong>
                  <p className="text-sm sm:text-base">
                    Implement RESTful APIs using Django REST Framework.
                  </p>
                  <pre className="bg-gray-100 p-2 mt-2 rounded text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap">
                    {`# views.py
from rest_framework import viewsets
from .models import Item
from .serializers import ItemSerializer

class ItemViewSet(viewsets.ModelViewSet):
    queryset = Item.objects.all()
    serializer_class = ItemSerializer

# urls.py
from django.urls import path, include
from rest_framework.routers import DefaultRouter
from .views import ItemViewSet

router = DefaultRouter()
router.register(r'items', ItemViewSet)
urlpatterns = [path('', include(router.urls))]`}
                  </pre>
                </li>
                <li>
                  <strong className="text-sm sm:text-base">Hour 3-4: Frontend Integration</strong>
                  <p className="text-sm sm:text-base">
                    Fetch and display inventory data in React.
                  </p>
                  <pre className="bg-gray-100 p-2 mt-2 rounded text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap">
                    {`# React component (e.g., Inventory.jsx)
import { useState, useEffect } from 'react';

const Inventory = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    fetch('http://192.250.229.92:3002/api/items/')
      .then(res => res.json())
      .then(data => setItems(data));
  }, []);
  return (
    <ul>{items.map(item => <li key={item.id}>{item.name}</li>)}</ul>
  );
};
export default Inventory;`}
                  </pre>
                </li>
                <li>
                  <strong className="text-sm sm:text-base">Hour 4-5: Create and Update Forms</strong>
                  <p className="text-sm sm:text-base">
                    Add forms for creating and updating items.
                  </p>
                  <pre className="bg-gray-100 p-2 mt-2 rounded text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap">
                    {`# React form
const AddItem = () => {
  const [form, setForm] = useState({ name: '', quantity: 0, price: 0 });
  const handleSubmit = (e) => {
    e.preventDefault();
    fetch('http://192.250.229.92:3002/api/items/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
  };
  return (
    <form onSubmit={handleSubmit}>
      <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
      <input type="number" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} />
      <input type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})} />
      <button type="submit">Add</button>
    </form>
  );
};`}
                  </pre>
                </li>
                <li>
                  <strong className="text-sm sm:text-base">Hour 5: Hands-On Exercise</strong>
                  <p className="text-sm sm:text-base">
                    Build a full CRUD interface and test it.
                  </p>
                  <pre className="bg-gray-100 p-2 mt-2 rounded text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap">
                    {`# Add delete functionality
const deleteItem = (id) => {
  fetch(\`http://192.250.229.92:3002/api/items/\${id}/\`, {
    method: 'DELETE',
  }).then(() => setItems(items.filter(item => item.id !== id)));
};

# Update functionality
const updateItem = (id, data) => {
  fetch(\`http://192.250.229.92:3002/api/items/\${id}/\`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(res => res.json()).then(updated => {
    setItems(items.map(item => item.id === id ? updated : item));
  });
};`}
                  </pre>
                  <p className="text-sm sm:text-base">
                    <strong>Troubleshooting:</strong> Ensure CORS is enabled on Django if frontend requests fail.
                  </p>
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Module 3 */}
        <div className="bg-orange-100 p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-2 sm:mb-0">
              Module 3: Advanced Features and Deployment
            </h2>
            <button
              onClick={() => handleModuleClick(3)}
              className="bg-green-600 text-white px-3 py-1 sm:px-4 sm:py-2 rounded-md hover:bg-green-700 transition-colors duration-300 w-full sm:w-auto"
            >
              {isModule3Open ? "Close Module" : "Start Module 3"}
            </button>
          </div>
          {isModule3Open && (
            <div className="mt-4 text-gray-600">
              <p className="mb-4 text-sm sm:text-base">
                Duration: 5 hours | Enhance the system and deploy it to a server.
              </p>
              <ul className="list-disc list-inside mb-4 space-y-4">
                <li>
                  <strong className="text-sm sm:text-base">Hour 1-2: Search and Filter</strong>
                  <p className="text-sm sm:text-base">
                    Add search and filter functionality.
                  </p>
                  <pre className="bg-gray-100 p-2 mt-2 rounded text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap">
                    {`# views.py
from rest_framework.decorators import api_view
from rest_framework.response import Response
from .models import Item
from .serializers import ItemSerializer

@api_view(['GET'])
def search_items(request):
    query = request.query_params.get('q', '')
    items = Item.objects.filter(name__icontains=query)
    serializer = ItemSerializer(items, many=True)
    return Response(serializer.data)

# urls.py
path('search/', search_items)

# React
const [search, setSearch] = useState('');
const filteredItems = items.filter(item => item.name.toLowerCase().includes(search.toLowerCase()));`}
                  </pre>
                </li>
                <li>
                  <strong className="text-sm sm:text-base">Hour 2-3: User Roles and Permissions</strong>
                  <p className="text-sm sm:text-base">
                    Implement role-based access control.
                  </p>
                  <pre className="bg-gray-100 p-2 mt-2 rounded text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap">
                    {`# models.py (add user profile)
from django.contrib.auth.models import User
from django.db import models

class Profile(models.Model):
    user = models.OneToOneField(User, on_delete=models.CASCADE)
    is_admin = models.BooleanField(default=False)

# views.py (permission check)
from rest_framework.permissions import IsAdminUser

class ItemViewSet(viewsets.ModelViewSet):
    queryset = Item.objects.all()
    serializer_class = ItemSerializer
    permission_classes = [IsAdminUser] # Restrict to admins`}
                  </pre>
                </li>
                <li>
                  <strong className="text-sm sm:text-base">Hour 3-4: Performance Optimization</strong>
                  <p className="text-sm sm:text-base">
                    Optimize queries and add caching.
                  </p>
                  <pre className="bg-gray-100 p-2 mt-2 rounded text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap">
                    {`# settings.py
CACHES = {
    'default': {
        'BACKEND': 'django.core.cache.backends.redis.RedisCache',
        'LOCATION': 'redis://127.0.0.1:6379/1',
    }
}

# views.py
from django.views.decorators.cache import cache_page

@cache_page(60 * 15) # Cache for 15 minutes
def item_list(request):
    items = Item.objects.all()
    serializer = ItemSerializer(items, many=True)
    return Response(serializer.data)`}
                  </pre>
                </li>
                <li>
                  <strong className="text-sm sm:text-base">Hour 4-5: Deployment</strong>
                  <p className="text-sm sm:text-base">
                    Deploy the app to a server.
                  </p>
                  <pre className="bg-gray-100 p-2 mt-2 rounded text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap">
                    {`# Prepare for deployment
python manage.py collectstatic

# Heroku deployment
heroku create inventory-app
git push heroku main
heroku run python manage.py migrate

# Test
open http://inventory-app.herokuapp.com`}
                  </pre>
                </li>
                <li>
                  <strong className="text-sm sm:text-base">Hour 5: Hands-On Exercise</strong>
                  <p className="text-sm sm:text-base">
                    Deploy and test the full system end-to-end.
                  </p>
                  <pre className="bg-gray-100 p-2 mt-2 rounded text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap">
                    {`# Add environment variables
heroku config:set DATABASE_URL=your_mysql_url
heroku config:set SECRET_KEY=your_secret_key

# Test endpoints
curl http://inventory-app.herokuapp.com/api/items/
curl -X POST -H "Content-Type: application/json" -d '{"name":"Test","quantity":10,"price":9.99}' http://inventory-app.herokuapp.com/api/items/`}
                  </pre>
                  <p className="text-sm sm:text-base">
                    <strong>Troubleshooting:</strong> If deployment fails, check logs with `heroku logs --tail`.
                  </p>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Code Entry Modal */}
      {showCodeForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Enter a code to unlock Module {currentModule}
            </h2>
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <input
                type="text"
                value={enteredCode}
                onChange={(e) => setEnteredCode(e.target.value)}
                maxLength="3"
                required
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter code"
                autoFocus
              />
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={handleCodeFormClose}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-300"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bootcamp;